import React, { useState, useMemo, useEffect } from "react";
import { Modal, Tabs } from "antd";
import { Line, Pie } from "@ant-design/plots";
import styles from "./CompareModal.module.scss";
import { indicatorList } from "Explore/Config/FilterConfig";

const CompareModal = ({ isOpen, onClose, selectedData, selectedIndicators = [] }) => {
  const [activeTab, setActiveTab] = useState("1");
  const metrics = useMemo(() => {
    const base = selectedIndicators?.length
      ? selectedIndicators
      : ["total_publications"];
    return base.map((value) => ({
      value,
      label: indicatorList.find((item) => item.value === value)?.label || value,
    }));
  }, [selectedIndicators]);
  const [pieMetric, setPieMetric] = useState(metrics[0]?.value || "total_publications");

  useEffect(() => {
    if (!metrics.some((item) => item.value === pieMetric)) {
      setPieMetric(metrics[0]?.value || "total_publications");
    }
  }, [metrics, pieMetric]);

  // Transform data for Grouped Column and Line (Long format)
  const longData = useMemo(() => {
    const result = [];
    selectedData.forEach((item) => {
      metrics.forEach((m) => {
        result.push({
          university: item.institution_name,
          metric: m.label,
          value: item.indicators[m.value] ?? 0,
        });
      });
    });
    return result;
  }, [selectedData, metrics]);



  const lineConfig = {
    data: longData,
    xField: "metric",
    yField: "value",
    colorField: "university",
    point: { shapeField: "circle", sizeField: 5 },
    interaction: { tooltip: { marker: true } },
    style: { lineWidth: 2 },
  };

  const pieData = useMemo(() => {
    return selectedData.map((item) => ({
      name: item.institution_name,
      value: item.indicators[pieMetric] ?? 0,
    }));
  }, [selectedData, pieMetric]);

  const pieConfig = {
    data: pieData,
    angleField: "value",
    colorField: "name",
    radius: 0.8,
    label: {
      text: "name",
      position: "outside",
    },
    legend: {
      position: "bottom",
    },
  };

  const items = [
    {
      key: "1",
      label: "Multi-line Chart",
      children: (
        <div className={styles.chartWrapper}>
          <div className={styles.chartContainer}>
            <Line {...lineConfig} />
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: "Pie Distribution",
      children: (
        <div className={styles.chartWrapper}>
          <div className={styles.pieControls}>
            <span className={styles.controlLabel}>Select Metric:</span>
            <div className={styles.metricGrid}>
              {metrics.map((m) => (
                <button
                  key={m.value}
                  className={`${styles.metricBtn} ${pieMetric === m.value ? styles.active : ""}`}
                  onClick={() => setPieMetric(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.chartContainer}>
            <Pie {...pieConfig} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Institution Comparison"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      className={styles.modal}
    >
      <div className={styles.content}>
        <p className={styles.description}>
          Comparing {selectedData.length} selected institutions across key metrics.
        </p>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items}
          className={styles.tabs}
        />
      </div>
    </Modal>
  );
};

export default CompareModal;
